/**
 * Content Security & Sanitization Utilities
 * 
 * Zabezpieczenia przed:
 * - XSS (Cross-Site Scripting)
 * - SQL Injection (przez walidację długości)
 * - Spam (rate limiting)
 * - Malicious URLs
 */

import DOMPurify from 'dompurify'

// ========================================
// TRUSTED TYPES POLICY
// ========================================

/**
 * Tworzy Trusted Type policy dla JSON-LD structured data
 * Używane przez SEO hook do bezpiecznego dodawania structured data
 */
let trustedTypesPolicy: TrustedTypePolicy | null = null

export function getTrustedTypesPolicy(): TrustedTypePolicy | null {
  // Return existing policy if already created
  if (trustedTypesPolicy) {
    return trustedTypesPolicy
  }

  // Check if Trusted Types are supported
  if (typeof window !== 'undefined' && window.trustedTypes) {
    try {
      trustedTypesPolicy = window.trustedTypes.createPolicy('default', {
        createHTML: (input: string) => DOMPurify.sanitize(input),
        createScript: (input: string) => input, // For JSON-LD, we trust our own JSON.stringify
        createScriptURL: (input: string) => input,
      })
      return trustedTypesPolicy
    } catch (error) {
      // Policy might already exist, try to get it
      console.warn('[TrustedTypes] Could not create policy:', error)
      return null
    }
  }

  return null
}

// ========================================
// KONFIGURACJA
// ========================================

/**
 * Limity długości treści
 */
export const CONTENT_LIMITS = {
  // Forum
  POST_TITLE_MIN: 3,
  POST_TITLE_MAX: 200,
  POST_CONTENT_MIN: 10,
  POST_CONTENT_MAX: 50000, // ~50KB text
  COMMENT_MIN: 1,
  COMMENT_MAX: 10000,
  TAG_MIN: 2,
  TAG_MAX: 30,
  MAX_TAGS: 10,
  
  // Profile
  USERNAME_MIN: 3,
  USERNAME_MAX: 30,
  BIO_MAX: 1000,
  FULL_NAME_MAX: 100,
  
  // Knowledge Base
  KB_TITLE_MAX: 300,
  KB_CONTENT_MAX: 100000, // ~100KB
  
  // Library
  BOOK_TITLE_MAX: 300,
  BOOK_DESCRIPTION_MAX: 5000,
} as const

/**
 * Dozwolone protokoły URL
 */
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:'] as const

/**
 * Niebezpieczne protokoły URL (blacklist)
 */
const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'about:',
] as const

/**
 * Rate limiting - liczba zapytań na minutę
 */
export const RATE_LIMITS = {
  POST_CREATE: 5,        // 5 postów/minutę
  COMMENT_CREATE: 10,    // 10 komentarzy/minutę
  VOTE: 30,              // 30 głosów/minutę
  PROFILE_UPDATE: 5,     // 5 aktualizacji profilu/minutę
} as const

// ========================================
// WALIDACJA URL
// ========================================

/**
 * Sprawdza czy URL jest bezpieczny
 * 
 * @param url - URL do sprawdzenia
 * @returns true jeśli URL jest bezpieczny
 */
export function isSafeURL(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false
  }

  // Trim whitespace
  url = url.trim()

  // Empty URLs are safe
  if (url === '') {
    return true
  }

  // Check for dangerous protocols
  const lowerUrl = url.toLowerCase()
  for (const protocol of DANGEROUS_PROTOCOLS) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn(`[Security] Blocked dangerous URL protocol: ${protocol}`)
      return false
    }
  }

  try {
    // Try to parse as URL
    const parsedUrl = new URL(url, window.location.origin)
    
    // Check if protocol is allowed
    const isAllowed = ALLOWED_PROTOCOLS.includes(parsedUrl.protocol as any)
    
    if (!isAllowed) {
      console.warn(`[Security] Blocked non-allowed URL protocol: ${parsedUrl.protocol}`)
    }
    
    return isAllowed
  } catch (error) {
    // If URL parsing fails, treat as relative URL
    // Relative URLs are safe
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return true
    }
    
    // Check if it looks like an anchor link
    if (url.startsWith('#')) {
      return true
    }
    
    console.warn(`[Security] Invalid URL format: ${url}`)
    return false
  }
}

/**
 * Sanityzuje URL - zwraca bezpieczną wersję lub fallback
 * 
 * @param url - URL do sanityzacji
 * @param fallback - Fallback URL (domyślnie '#')
 * @returns Bezpieczny URL
 */
export function sanitizeURL(url: string, fallback: string = '#'): string {
  if (isSafeURL(url)) {
    return url
  }
  
  console.warn(`[Security] Replaced unsafe URL with fallback: ${url}`)
  return fallback
}

// ========================================
// WALIDACJA TREŚCI
// ========================================

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

/**
 * Waliduje tytuł posta
 */
export function validatePostTitle(title: string): ValidationResult {
  const errors: string[] = []
  
  if (!title || typeof title !== 'string') {
    errors.push('Tytuł jest wymagany')
    return { isValid: false, errors }
  }
  
  const trimmed = title.trim()
  
  if (trimmed.length < CONTENT_LIMITS.POST_TITLE_MIN) {
    errors.push(`Tytuł musi mieć co najmniej ${CONTENT_LIMITS.POST_TITLE_MIN} znaki`)
  }
  
  if (trimmed.length > CONTENT_LIMITS.POST_TITLE_MAX) {
    errors.push(`Tytuł może mieć maksymalnie ${CONTENT_LIMITS.POST_TITLE_MAX} znaków`)
  }
  
  // Check for suspicious patterns
  if (/<script|javascript:|data:|on\w+=/i.test(trimmed)) {
    errors.push('Tytuł zawiera niedozwolone znaki')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Waliduje treść posta
 */
export function validatePostContent(content: string): ValidationResult {
  const errors: string[] = []
  
  if (!content || typeof content !== 'string') {
    errors.push('Treść jest wymagana')
    return { isValid: false, errors }
  }
  
  const trimmed = content.trim()
  
  if (trimmed.length < CONTENT_LIMITS.POST_CONTENT_MIN) {
    errors.push(`Treść musi mieć co najmniej ${CONTENT_LIMITS.POST_CONTENT_MIN} znaków`)
  }
  
  if (trimmed.length > CONTENT_LIMITS.POST_CONTENT_MAX) {
    errors.push(`Treść może mieć maksymalnie ${CONTENT_LIMITS.POST_CONTENT_MAX} znaków`)
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Waliduje komentarz
 */
export function validateComment(content: string): ValidationResult {
  const errors: string[] = []
  
  if (!content || typeof content !== 'string') {
    errors.push('Komentarz jest wymagany')
    return { isValid: false, errors }
  }
  
  const trimmed = content.trim()
  
  if (trimmed.length < CONTENT_LIMITS.COMMENT_MIN) {
    errors.push(`Komentarz musi mieć co najmniej ${CONTENT_LIMITS.COMMENT_MIN} znak`)
  }
  
  if (trimmed.length > CONTENT_LIMITS.COMMENT_MAX) {
    errors.push(`Komentarz może mieć maksymalnie ${CONTENT_LIMITS.COMMENT_MAX} znaków`)
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Waliduje tagi
 */
export function validateTags(tags: string[]): ValidationResult {
  const errors: string[] = []
  
  if (!Array.isArray(tags)) {
    errors.push('Tagi muszą być tablicą')
    return { isValid: false, errors }
  }
  
  if (tags.length > CONTENT_LIMITS.MAX_TAGS) {
    errors.push(`Maksymalnie ${CONTENT_LIMITS.MAX_TAGS} tagów`)
  }
  
  for (const tag of tags) {
    if (typeof tag !== 'string') {
      errors.push('Wszystkie tagi muszą być tekstem')
      continue
    }
    
    const trimmed = tag.trim()
    
    if (trimmed.length < CONTENT_LIMITS.TAG_MIN) {
      errors.push(`Tag musi mieć co najmniej ${CONTENT_LIMITS.TAG_MIN} znaki`)
    }
    
    if (trimmed.length > CONTENT_LIMITS.TAG_MAX) {
      errors.push(`Tag może mieć maksymalnie ${CONTENT_LIMITS.TAG_MAX} znaków`)
    }
    
    // Only alphanumeric and basic characters
    if (!/^[\w\d\-_ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+$/i.test(trimmed)) {
      errors.push(`Tag "${trimmed}" zawiera niedozwolone znaki`)
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

// ========================================
// SANITYZACJA HTML
// ========================================

/**
 * Konfiguracja DOMPurify dla Markdown content
 */
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ['strong', 'em', 'code', 'a', 'br', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto):)/i,
}

/**
 * Sanityzuje HTML content używając DOMPurify
 * 
 * @param html - HTML do sanityzacji
 * @returns Bezpieczny HTML
 */
export function sanitizeHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return ''
  }
  
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG)
}

const DOCUMENT_HTML_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'strong',
    'em',
    'u',
    'span',
    'div',
    'br',
    'hr',
    'ol',
    'ul',
    'li',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'h1',
    'h2',
    'h3',
    'h4',
    'blockquote',
    'a',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'colspan', 'rowspan', 'class', 'scope'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto):)/i,
}

export function sanitizeDocumentHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return ''
  }

  return DOMPurify.sanitize(html, DOCUMENT_HTML_CONFIG)
}

/**
 * Sanityzuje plaintext - usuwa tylko najbardziej niebezpieczne rzeczy
 * Nie konwertuje do HTML, tylko czyści tekst
 * 
 * @param text - Tekst do sanityzacji
 * @returns Bezpieczny tekst
 */
export function sanitizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return ''
  }
  
  // Remove null bytes
  let sanitized = text.replace(/\0/g, '')
  
  // Remove control characters except newlines and tabs
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
  
  return sanitized.trim()
}

// ========================================
// RATE LIMITING
// ========================================

/**
 * Simple client-side rate limiter
 * Uwaga: To jest tylko pomocnicze zabezpieczenie.
 * Prawdziwy rate limiting MUSI być po stronie serwera!
 */
class ClientRateLimiter {
  private requests: Map<string, number[]> = new Map()
  
  /**
   * Sprawdza czy akcja jest dozwolona
   * 
   * @param key - Klucz akcji (np. 'post_create')
   * @param limit - Limit zapytań na minutę
   * @returns true jeśli akcja dozwolona
   */
  checkLimit(key: string, limit: number): boolean {
    const now = Date.now()
    const windowMs = 60 * 1000 // 1 minute
    
    // Get or create request history
    const history = this.requests.get(key) || []
    
    // Remove old requests outside window
    const recentRequests = history.filter(time => now - time < windowMs)
    
    // Check if limit exceeded
    if (recentRequests.length >= limit) {
      console.warn(`[RateLimit] Limit exceeded for ${key}: ${recentRequests.length}/${limit}`)
      return false
    }
    
    // Add current request
    recentRequests.push(now)
    this.requests.set(key, recentRequests)
    
    return true
  }
  
  /**
   * Czyści historię dla danego klucza
   */
  clear(key: string): void {
    this.requests.delete(key)
  }
  
  /**
   * Czyści całą historię
   */
  clearAll(): void {
    this.requests.clear()
  }
}

// Export singleton instance
export const rateLimiter = new ClientRateLimiter()

// ========================================
// UTILITY FUNCTIONS
// ========================================

/**
 * Skraca tekst do określonej długości
 * 
 * @param text - Tekst do skrócenia
 * @param maxLength - Maksymalna długość
 * @param suffix - Suffix (domyślnie '...')
 * @returns Skrócony tekst
 */
export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (!text || text.length <= maxLength) {
    return text
  }
  
  return text.substring(0, maxLength - suffix.length) + suffix
}

/**
 * Normalizuje białe znaki w tekście
 * 
 * @param text - Tekst do normalizacji
 * @returns Znormalizowany tekst
 */
export function normalizeWhitespace(text: string): string {
  if (!text || typeof text !== 'string') {
    return ''
  }
  
  // Replace multiple spaces with single space
  let normalized = text.replace(/\s+/g, ' ')
  
  // Trim
  normalized = normalized.trim()
  
  return normalized
}

/**
 * Sprawdza czy tekst zawiera spam patterns
 * 
 * @param text - Tekst do sprawdzenia
 * @returns true jeśli wykryto spam
 */
export function detectSpam(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return false
  }
  
  const spamPatterns = [
    // Excessive capitalization
    /[A-Z]{10,}/,
    // Excessive exclamation marks
    /!{5,}/,
    // Excessive URLs
    /(https?:\/\/[^\s]+){5,}/i,
    // Common spam words (customize based on your needs)
    /\b(viagra|cialis|casino|lottery|winner|congratulations)\b/i,
  ]
  
  for (const pattern of spamPatterns) {
    if (pattern.test(text)) {
      console.warn('[Spam] Detected spam pattern in text')
      return true
    }
  }
  
  return false
}

// ========================================
// EXPORTS
// ========================================

export default {
  // Trusted Types
  getTrustedTypesPolicy,
  
  // URL
  isSafeURL,
  sanitizeURL,
  
  // Validation
  validatePostTitle,
  validatePostContent,
  validateComment,
  validateTags,
  
  // Sanitization
  sanitizeHTML,
  sanitizeText,
  
  // Rate limiting
  rateLimiter,
  
  // Utils
  truncateText,
  normalizeWhitespace,
  detectSpam,
  
  // Constants
  CONTENT_LIMITS,
  RATE_LIMITS,
}

